import type { IbhPartsRequestDto } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhShape } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type {
  IbhCDimensionsDto,
  IbhDDimensionsDto,
  IbhEasyClipDimensionsDto,
  IbhFlangeBearingDimensionsDto,
  IbhFsgDimensionsDto,
  IbhKDimensionsDto,
  IbhPistonRingDimensionsDto,
  IbhSleeveBearingDimensionsDto,
  IbhSplitBearingAntiRotationDimensionsDto,
  IbhSplitBearingDimensionsDto,
  IbhTeDimensionsDto,
  IbhThrustWasherDimensionsDto,
  IbhTwoHoleFlangeBearingDimensionsDto,
  IbhTwoHoleFlangeBearingPreloadDimensionsDto,
  IbhVfDimensionsDto,
  IbhVsDimensionsDto,
} from '@igus/iglidur-bearing-hub-api';
import type { LengthCode } from '@icommerce/units';

export type Bevels = {
  f1: Bevel;
  f2: Bevel;
  f3: Bevel;
  f4: Bevel;
};

export type Bevel = {
  angle: number;
  length: number;
};

export type FlatBevelData = {
  'f1.length': number;
  'f2.length': number;
  'f3.length': number;
  'f4.length': number;
  'f1.angle': number;
  'f2.angle': number;
  'f3.angle': number;
  'f4.angle': number;
};

export type IbhDimensions =
  | IbhThrustWasherDimensionsDto
  | IbhSleeveBearingDimensionsDto
  | IbhKDimensionsDto
  | IbhDDimensionsDto
  | IbhVsDimensionsDto
  | IbhTeDimensionsDto
  | IbhFlangeBearingDimensionsDto
  | IbhVfDimensionsDto
  | IbhSplitBearingDimensionsDto
  | IbhSplitBearingAntiRotationDimensionsDto
  | IbhPistonRingDimensionsDto
  | IbhEasyClipDimensionsDto
  | IbhCDimensionsDto
  | IbhTwoHoleFlangeBearingPreloadDimensionsDto
  | IbhTwoHoleFlangeBearingDimensionsDto
  | IbhFsgDimensionsDto;

export type DimensionKey = (typeof dimensionKey)[keyof typeof dimensionKey];

export type DiameterValue = {
  unit: LengthCode;
  amount: number;
  calculations?: {
    [key in 'INCH' | 'METRIC']: number;
  };
};

export const dimensionKey = {
  b1: 'b1',
  b2: 'b2',
  b3: 'b3',
  d1: 'd1',
  d2: 'd2',
  d3: 'd3',
  d4: 'd4',
  d5: 'd5',
  d6: 'd6',
  d7: 'd7',
  r: 'r',
  slotAngle: 'slotAngle',
  sb: 'sb',
  b2d3: 'b2d3',
  b2d4: 'b2d4',
  h: 'h',
  t: 't',
  frD: 'frD',
  frW: 'frW',
  frT: 'frT',
} as const;

export const bevelKey = {
  'f1.length': 'f1.length',
  'f2.length': 'f2.length',
  'f3.length': 'f3.length',
  'f4.length': 'f4.length',
  'f1.angle': 'f1.angle',
  'f2.angle': 'f2.angle',
  'f3.angle': 'f3.angle',
  'f4.angle': 'f4.angle',
} as const;

export const ShapeDimensionsDefinition = {
  [IbhShape.S]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1],
  [IbhShape.F]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2],
  [IbhShape.T]: [
    dimensionKey.d1,
    dimensionKey.d2,
    dimensionKey.d4,
    dimensionKey.d5,
    dimensionKey.d6,
    dimensionKey.b1,
    dimensionKey.h,
  ],
  [IbhShape.FL]: [
    dimensionKey.d1,
    dimensionKey.d2,
    dimensionKey.d3,
    dimensionKey.d4,
    dimensionKey.d5,
    dimensionKey.d6,
    dimensionKey.d7,
    dimensionKey.b1,
    dimensionKey.b2,
    dimensionKey.b3,
    dimensionKey.r,
  ],
  [IbhShape.PR]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1, dimensionKey.slotAngle, dimensionKey.b2],
  [IbhShape.EC]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b2, dimensionKey.b3, dimensionKey.sb],

  [IbhShape.C]: [
    dimensionKey.d1,
    dimensionKey.d2,
    dimensionKey.d3,
    dimensionKey.d4,
    dimensionKey.b2d3,
    dimensionKey.b2d4,
    dimensionKey.b3,
  ],
  [IbhShape.Y]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2],
  [IbhShape.YK]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2],
  [IbhShape.VFL]: [
    dimensionKey.d1,
    dimensionKey.d2,
    dimensionKey.d3,
    dimensionKey.d4,
    dimensionKey.d5,
    dimensionKey.d6,
    dimensionKey.d7,
    dimensionKey.b1,
    dimensionKey.b2,
    dimensionKey.b3,
    dimensionKey.r,
  ],
  [IbhShape.TE]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.h, dimensionKey.t],
  [IbhShape.D]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1, dimensionKey.b2],
  [IbhShape.VF]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2, dimensionKey.b3],
  [IbhShape.K]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2, dimensionKey.b3],
  [IbhShape.VS]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1, dimensionKey.b3],
  [IbhShape.FSG]: [
    dimensionKey.d1,
    dimensionKey.d2,
    dimensionKey.d3,
    dimensionKey.b1,
    dimensionKey.b2,
    dimensionKey.frD,
    dimensionKey.frW,
    dimensionKey.frT,
  ],
};

export const ShapeDimensionsDefinitionForm: { [key in IbhShape]: DimensionKey[] } = {
  ...ShapeDimensionsDefinition,
  [IbhShape.T]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1], // This shape has optional d4 and d5 param which is not configurable
  [IbhShape.FL]: [dimensionKey.d1], // This shape has a limited number of fields
  [IbhShape.Y]: [dimensionKey.d1, dimensionKey.d2], // This shape has a limited number of fields
  [IbhShape.VFL]: [dimensionKey.d1], // This shape has a limited number of fields
  [IbhShape.TE]: [dimensionKey.d1], // This shape has a limited number of fields
  [IbhShape.D]: [dimensionKey.d1, dimensionKey.d2], // This shape has a limited number of fields
  [IbhShape.VF]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2], // This shape has a limited number of fields
  [IbhShape.K]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1], // This shape has a limited number of fields
  [IbhShape.VS]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1], // This shape has a limited number of fields
  [IbhShape.FSG]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2], // This shape has a limited number of fields
};

export const ShapeDimensionsDefinitionUsp: { [key in IbhShape]: DimensionKey[] } = {
  [IbhShape.S]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1],
  [IbhShape.F]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2],
  [IbhShape.FL]: [dimensionKey.d1],
  [IbhShape.VFL]: [dimensionKey.d1],
  [IbhShape.Y]: [dimensionKey.d1, dimensionKey.b1, dimensionKey.d3],
  [IbhShape.YK]: [dimensionKey.d1, dimensionKey.b1, dimensionKey.d3],
  [IbhShape.C]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b3],
  [IbhShape.D]: [dimensionKey.d1, dimensionKey.b1, dimensionKey.d3],
  [IbhShape.T]: [
    dimensionKey.d1,
    dimensionKey.d2,
    dimensionKey.d4,
    dimensionKey.d5,
    dimensionKey.d6,
    dimensionKey.b1,
    dimensionKey.h,
  ],
  [IbhShape.PR]: [dimensionKey.d1, dimensionKey.d2],
  [IbhShape.TE]: [dimensionKey.d1, dimensionKey.d2],
  [IbhShape.EC]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b3],
  [IbhShape.VF]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1],
  [IbhShape.K]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1],
  [IbhShape.VS]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1],
  [IbhShape.FSG]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2],
};

export const isBearingParamsComplete = (part: IbhPartsRequestDto): boolean => {
  const dimensions = ShapeDimensionsDefinitionForm[part.shape];
  return dimensions.every(
    dimension => dimension in part && typeof part[dimension as keyof IbhPartsRequestDto] !== 'undefined'
  );
};

export const getDimensionLabel = (dimension: DimensionKey, shape: IbhShape): string => {
  switch (dimension) {
    case dimensionKey.b2d3:
      return 'b2.2';
    case dimensionKey.b2d4:
      return 'b2.1';
    case dimensionKey.slotAngle:
      return 'T (°)';
    case dimensionKey.b1:
      return shape === IbhShape.T ? 's' : String(dimension);
    default:
      return String(dimension);
  }
};
